'use client'

import * as React from 'react'
import { DataTable } from '@/components/data-table/data-table'
import { useDataTable } from '@/hooks/use-data-table'
import { DataTableSortList } from '@/components/data-table/data-table-sort-list'
import { Skeleton } from '@/components/ui/skeleton'
import { TableRow, TableCell } from '@/components/ui/table'

import { useEmailTemplatesTableColumns } from './email-templates-table-columns'
import { EmailTemplatesTableActions } from './email-templates-table-actions'
import { MainTableState } from '@/lib/utils/table-state-utils'
import type { TableConfig } from '@/types/data-table'
import { DEFAULT_SKELETON_ROWS } from '@/config/data-table'
import type { EmailTemplateItem } from '@/lib/api/emailTemplates'
import { DeleteEmailTemplateModal } from './delete-email-template-modal'
import { useStoreNavigate } from '@/hooks/use-app-navigate'

interface EmailTemplatesTableProps {
	templates: EmailTemplateItem[]
	loading: boolean
	onTableStateChange: (state: MainTableState) => void
	onRefetch: () => void
	config?: TableConfig
}

const EmailTemplateTableSkeletonRow = () => (
	<TableRow className="hover:bg-transparent">
		{/* Select checkbox column */}
		<TableCell className="w-[40px]">
			<Skeleton className="h-4 w-4" />
		</TableCell>
		{/* Name column (with subject) */}
		<TableCell>
			<div className="space-y-1.5">
				<Skeleton className="h-4 w-48" />
				<Skeleton className="h-3 w-64" />
			</div>
		</TableCell>
		{/* Type column */}
		<TableCell>
			<Skeleton className="h-5 w-24" />
		</TableCell>
		{/* Active/Status column */}
		<TableCell>
			<Skeleton className="h-5 w-16" />
		</TableCell>
		{/* Created date column */}
		<TableCell>
			<Skeleton className="h-4 w-24" />
		</TableCell>
		{/* ID column (copyable, at end per convention) */}
		<TableCell>
			<Skeleton className="h-5 w-20" />
		</TableCell>
		{/* Actions column */}
		<TableCell className="w-[40px]">
			<Skeleton className="h-8 w-8" />
		</TableCell>
	</TableRow>
)

const createSkeletonRows = (count: number) => (
	<>
		{Array.from({ length: count }).map((_, i) => (
			<EmailTemplateTableSkeletonRow key={i} />
		))}
	</>
)

/**
 * EmailTemplatesTable component for displaying email templates with DataTable functionality
 */
export function EmailTemplatesTable({
	templates,
	loading,
	onTableStateChange,
	onRefetch,
	config = {},
}: EmailTemplatesTableProps) {
	const { storeRoutes, navigate } = useStoreNavigate()
	const [templateId, setTemplateId] = React.useState<string | undefined>(undefined)

	const columns = useEmailTemplatesTableColumns({
		onEditTemplate: (id: string) => {
			navigate(storeRoutes.EMAIL_TEMPLATE_EDIT(id))
		},
		onDeleteTemplate: (id: string) => setTemplateId(id),
	})

	const { table } = useDataTable({
		data: templates,
		columns,
		total: templates.length,
		config: { ...config, clientMode: true, defaultSorting: [{ id: 'createdAt', desc: true }] },
		onTableStateChange,
		getRowId: (row: EmailTemplateItem) => row.id,
	})

	return (
		<DataTable
			table={table}
			isLoading={loading}
			skeletonRows={createSkeletonRows(config.skeletonRows || DEFAULT_SKELETON_ROWS)}
		>
			<EmailTemplatesTableActions table={table} onRefresh={onRefetch} isLoading={loading} />
			<DataTableSortList table={table} align="end" />
			<DeleteEmailTemplateModal templateId={templateId} onClose={() => setTemplateId(undefined)} onSuccess={onRefetch} />
		</DataTable>
	)
}

